import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import {GestureEventListeners} from '@polymer/polymer/lib/mixins/gesture-event-listeners';
import template from './ns-billing.html';
import {PaymentService, PaymentDto, PaymentType, PaymentAdapter} from '@nuskin/ns-shop';
import {TranslationsUtil, ConfigService, NsRestError, moment} from '@nuskin/ns-util';
import {billingTranslations} from './translations.js';
import {rawPaymentList} from './test-payment-list.js';
import {testSelectedCard} from './test-selected-card.js';
import {allowedPayments} from './test-allowed-payments.js';
import '../temp/ns-form-temp';
/**
 * Image Imports
 */
import deleteIcon from '../img/icon-trash.png';
import closeIcon from '../img/ic-close-small.svg';
import warningIcon from '../img/warning-icon.svg';

/**
 * Displays the credit card associated with a user's ADR, and allows the user to change
 * to any credit card saved in the system, or add a new one.
 *
 * <section>
 *  <h2>Content Guidelines</h2>
 *  <p>Here are some guidelines on how to properly use this element.</p>
 * </section>
 *
 * <h2>Billing Component Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-billing is-demo></ns-billing>
 *   </template>
 * </demo-snippet>
 */
export class NsBilling extends GestureEventListeners(PolymerElement) {
  static get is () {
    return 'ns-billing';
  }

  static get template () {
    let htmlElement = document.createElement('TEMPLATE');
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  static get properties () {
    return {
      /**
       * The response object for a GET request is assigned to this property.
       * @type {PaymentDTO[]}
       */
      getResponse: {
        type: Object,
        observer: '_handleGetResponse'
      },
      /**
       * The response object for a POST request is assigned to this property.
       * @type {PaymentDTO}
       */
      postResponse: {
        type: Object,
        observer: '_handlePostResponse'
      },
      /**
       * The response object for a PUT request is assigned to this property.
       * @type {PaymentDTO}
       */
      putResponse: {
        type: Object,
        observer: '_handlePutResponse'
      },
      /**
       * The response object for a DELETE request is assigned to this property.
       */
      deleteResponse: {
        type: Object,
        observer: '_handleDeleteResponse'
      },
      /**
       * Object used to share data between ns-billing and ns-adp-wrapper.
       * @type {BillingDataObject}
       */
      billingDataObject: {
        type: Object,
        notify: true,
        observer: '_mapPaymentTypes'
      },
      /**
       * Disables buttons and functionality if adr is not editable
       */
      isNotEditable: {
        type: Boolean,
        default: false
      },
      /** Country for translations
      * @example:US, JP, CN
      */
      country: {
        type: String
      },
      /**
      * Language for translations
      * @example: en, es, fr
      */
      language: {
        type: String
      },
      /**
       * Currently selected view: selected payment, all payments, or payment form.
       */
      currentView: {
        type: String,
        value: 'selectedPayment'
      },
      /**
       * Array containing the index of the current selection of the card type dropdown. [0] = Visa, [1] = MasterCard,
       * [2] = Discover, [3] = Amex.
       */
      selectedPaymentType: {
        type: Object,
        notify: true,
        observer: '_dropdownSelectionChanged'
      },
      /**
       * Encoded value of delete icon used for passing this value to the template
       */
      deleteIcon: {
        type: String,
        value: deleteIcon
      },
      /**
       * Encoded value of close icon used for passing this value to the template
       */
      closeIcon: {
        type: String,
        value: closeIcon
      },
      /**
       * Encoded value of warning icon used for passing this value to the template
       */
      warningIcon: {
        type: String,
        value: warningIcon
      },
      /**
       * Determines if the payment form should show the "save as primary credit card" checkbox.
       * This allows the user to edit the defaultPayment field of the card object.
       */
      showPrimaryCardCheckbox: {
        type: Boolean,
        value: false
      },
      /**
       * Specifies which page the billing component is being used for - Profile, Adp manage, or Checkout.
       * Some items are shown or hidden depending on the value of this.pageName.
       */
      pageName: {
        type: String
      },
      /**
       * Allows disabling any delete buttons,for testing purposes.
       */
      enableDelete: {
        type: Boolean,
        value: true
      },
      /**
       * Payment types allowed based on market
       * @type {PaymentTypeContext[]}
       */
      paymentTypesAllowed: {
        type: Array,
        observer: '_mapPaymentForms'
      },
      /**
       * Id of the selected payment type
       */
      paymentTypeId: {
        type: Number
      },
      /**
       * Populates component with test data and prevents any unnecessary network calls.
       */
      isDemo: {
        type: Boolean
      },
      /**
       * Setting this to true gives the custom attribute to ns-buttons contained within ns-billing.
       * This allows for style overrides via a style module.
       */
      useCustomButtons: {
        type: Boolean,
        value: false
      },
      /**
       * List of payments saved to the user's profile
       */
      savedPayments: {
        type: Array,
        value: () => []
      },
      /**
       * Holds payment lists and fires observer
       * @type {PaymentDTO[]}
       */
      paymentList: {
        type: Array,
        value: () => [],
        observer: '_mapPaymentForms'
      },
      /**
       * Holds credit card arrays and fires observer
       * @type {PaymentTypeContext[]}
       */
      creditCards: {
        type: Array,
        observer: '_mapPaymentForms'
      },
      /**
       * Page upon which this component appears. Used for translations.
       */
      webPageName: {
        type: String,
        value: 'shop/adp-manage'
      },
      /**
       * Translations from the server. Triggers the translation assignment process.
       * If undefined and shouldAwaitTranslations=false, this component will fetch its own strings.
       */
      serverTranslations: {
        type: Object,
        observer: '_assignTranslations'
      },
      /**
       * If false, it will start the translation assignment process without waiting.
       * If true, it will wait for serverTranslations to be set before starting the translation assignment process.
       * If serverTranslations is defined already, the component will behave as if this property is true.
       */
      shouldAwaitTranslations: {
        type: Boolean,
        value: false
      },
      /**
       * Collection of toast message objects, shared with the parent (ns-adp-wrapper).
       * Messages in this collection are displayed by the parent.
       */
      toastMessages: {
        type: Array,
        notify: true
      },
      /**
       * Config to hide show save as primary credit card checkbox
       */
      hidePrimaryCardCheckbox: {
        type: Boolean,
        value: false
      },
      /**
       * holder for the feature flags enabled for the user 
       */
      featureFlags: {
        type: Object,
        value: {}
      }
    };
  }

  static get observers () {
    return [
      /**
       * If it's the adr manage page, we need to make sure both paymentList and billingDataObject are defined before
       * running the observer. computeExpiredData uses the nextDeliveryDate from billingDataObject. If it's not adr manage,
       * billingDataObject does not need to be defined.
       */
      '_computeExpiredData(paymentList, billingDataObject)',
      '_billingDataObjectChanged(billingDataObject, paymentList, creditCards)',
      '_compareADRPaymentsToList(billingDataObject, savedPayments, paymentTypesAllowed)'
    ];
  }

  ready () {
    super.ready();
    if (!this.TRANSLATION_HELPER) this._localize();
    this.paymentService = PaymentService;
    if (!this.isDemo) {
      this._getSavedPayments();
    } else {
      this.getResponse = [];
      /**
       * @type {PaymentDTO[]}
       */
      this.paymentList = rawPaymentList;
      this.paymentTypesAllowed = allowedPayments;
      this.selectedCard = testSelectedCard;
    }

    if (this.pageName === 'Profile') {
      this.currentView = 'allPayments';
    }
    this.top = this.shadowRoot.querySelector('header-row-tag');
    this.isPrimaryCard = false;
  }

  /**
   * Sets language and country properties and assigns translations to labels
   */
  _localize () {
    this.TRANSLATION_HELPER = new TranslationsUtil(this.language, this.country, billingTranslations);
    this.language = this.TRANSLATION_HELPER.language;
    this.country = this.TRANSLATION_HELPER.country;
    if (!this.shouldAwaitTranslations) this._assignTranslations();
  }

  /**
   * Gathers all translations and maps them to properties.
   * Any strings set as html attributes take precedence over translations from the server,
   * which in turn take precedence over the default translations in the translations file.
   */
  async _assignTranslations () {
    if (!this.TRANSLATION_HELPER) this._localize();
    this.serverTranslations = this.serverTranslations || await this.TRANSLATION_HELPER.getServerTranslations(this.webPageName);
    let allTranslations = Object.assign({}, this.TRANSLATION_HELPER.getDefaultTranslations(), this.serverTranslations);
    Object.entries(allTranslations).forEach(([key, value]) => {
      this[key] = this[key] || value;
    });
  }

  _getSavedPayments() {
    PaymentService.getSavedPaymentsInCurrentMarket({ orderType: 'ADR MANAGE' })
      .then(response => { this.getResponse = response; })
      .finally(() => { if (!this.savedPayments) this.savedPayments = [] })
      .catch(err => { this._handleError(err); });
  }

  /**
   * Get payment types allowed
   */
  _getAllowedPaymentTypes (orderType) {
    PaymentService.getAllowedPaymentTypes({ 
      orderType: orderType, 
      shipToCntryCode: this.billingDataObject.shipToCountryCode, 
      cntryCode: this.billingDataObject.countryCode
    })
      .then(response => {
        this.paymentTypesAllowed = response;
        if (this.paymentTypesAllowed) {
          this._mapPaymentTypes();
        }
        this.selectedPaymentType = this.paymentTypesAllowed ? this.paymentTypesAllowed[0] : null;
      })
      .catch(err => { this._handleError(err); });
  }

  allowEditPayment(payment) { // sections.payment.newPayment.savePayment
    const EMPTY_PAYMENT = 'emptyPayment';
    let allowEdit = false;

    if (payment.paymentTypeId !== PaymentType.CEDN &&
      payment.paymentTypeId !== PaymentType.IDEA &&
        ((payment.formName !== EMPTY_PAYMENT && payment.paymentTypeId !== PaymentType.SDD) ||
        (payment.paymentTypeId === PaymentType.SDD && !payment.savePayment)))
    {
      allowEdit = true;
    }
    return allowEdit;
  }

  /**
   * Set up the types of payment for each user's saved payment, and identify if the saved payment is a credit or not.
   */
  _mapPaymentForms () {
    if (this.paymentList && this.paymentTypesAllowed && this.creditCards) {
      for (let idx = this.paymentList.length - 1; idx >= 0 ; idx--) {
        let obj1 = this.paymentList[idx];
        const paymentType = this.paymentTypesAllowed.filter(obj => obj.id === obj1.paymentTypeId)[0];
        if (paymentType) {
          this.set(`paymentList.${idx}.formName`, paymentType.formName);
          this.set(`paymentList.${idx}.isCreditCard`, this._isCreditCard(obj1));
          const showPaymentEdit = this.allowEditPayment(obj1)
          this.set(`paymentList.${idx}.showPaymentEdit`, showPaymentEdit);
        } else {
          this.paymentList.splice(idx, 1);
        }
      }
    }
  }

  /**
   * Observer when billingDataObject response is changed.
   * @param {BillingDataObject} billingDataObject
   * @param {PaymentDTO[]} paymentList
   * @param {PaymentTypeContext[]} creditCards
   */
  _billingDataObjectChanged (billingDataObject, paymentList, creditCards) {
    if (billingDataObject && !this.gotBillingData) {
      this.gotBillingData = true;
      this._getAllowedPaymentTypes('ADR MANAGE');
    }

    if (!(billingDataObject && paymentList && creditCards)) {
      return;
    }

    let payments = billingDataObject.payment;
    // ADR does not have any payments associated with it, show all payments
    if (!payments) {
      this._viewAllPaymentMethods();
      return;
    }

    /**
     * selectedCard must be a copy. We're adding fields, and if the original card contains them, it will
     * mess up the ADR put request. Moreover, we have to make the copy before adding the additional data
     * fields - otherwise the two way binding will not work.
     * @type {CreditCard}
     * @property {boolean} isCreditCard
     */
    const newSelectedCard = JSON.parse(JSON.stringify(payments[0].CreditCard));
    newSelectedCard.isCreditCard = this._isCreditCard(payments[0]);
    let tempObjType, tempPaymentType;

    // Process Payment Type: CreditCard or WIRE
    if (newSelectedCard.isCreditCard) {
      tempObjType = 'ofsId';
      tempPaymentType = billingDataObject.payment[0].CreditCard.Type;
      this._selectedCardAdditionalData(newSelectedCard);

      //Cedyna cards don't expire but were kicking off the modal
      const isCedyna = newSelectedCard.Type == 'CEDN' || newSelectedCard.Type == 'Cedyna' || newSelectedCard.Type == 'CR';
      if (!isCedyna) {
        const expDate = new Date(`20${newSelectedCard.expiration.substring(3, 5)}`, newSelectedCard.expiration.substring(0, 2));
        const nextDeliveryDate = new Date(billingDataObject.nextDeliveryDate);
  
        // Create hash of credit card info.
        // This hash is used to determine whether this function is being invoked multiple times with the same card,
        // to avoid performing certain actions more than once for a given card.
        const CC = billingDataObject.payment[0].CreditCard;
        const cardHash = CC.CardNumber + CC.Name + CC.ExpirationMonth + CC.ExpirationYear;
  
        if (billingDataObject.Editable && moment(expDate).diff(moment(nextDeliveryDate), 'months') < 1 && cardHash !== this.prevExpiredCard) {
          this.dispatchEvent(new CustomEvent('closeAdpWrapperOverlay', {detail: {closeOverlay: false}}));
          this.$.expirationDialog.open();
          this.prevExpiredCard = cardHash;
        }
      }
    } else if (billingDataObject.payment[0].type === 'WIRE' || billingDataObject.payment[0].type === 'COMPANYCREDIT' || billingDataObject.payment[0].type === 'DIRECTDEBIT') {
      if (billingDataObject.payment[0].type === 'DIRECTDEBIT') {
        this._selectedBankAdditionalData(newSelectedCard, payments)
      }
      tempObjType = 'paymentClass';
      tempPaymentType = billingDataObject.payment[0].type;
    }

    // See if Payment is allowed in paymentTypesAllowed List, if not found show all payments
    const foundCard = this.paymentTypesAllowed.find(obj => obj[tempObjType] === tempPaymentType);
    if (!foundCard || !tempObjType || !tempPaymentType) {
      this._viewAllPaymentMethods();
      // Alert user of invalid payment
      if (this.invalidPaymentWarningHeader && !this.toastMessages.find(m => m.invalidPmtFlag)) {
        this._addToastMessage('error', {
          header: this.invalidPaymentWarningHeader,
          message: this.invalidPaymentWarningMessage,
          scrollIntoView: true,
          /*
          _billingDataObjectChanged can be called multiple times during loading of a single ADR,
          even with no user-initiated change to the billing information. Therefore, to prevent
          multiple redundant copies of this message from being displayed, an identifying flag is
          added to this toast message.
          */
          invalidPmtFlag: true
        });
      }
      return;
    }
    // Payment is of allowed type. Check if there is disclaimer text for this payment type.
    else if (this.paymentMethodDisclaimers) {
      const pmtTypeId = foundCard.id;
      const disclaimers = this.paymentMethodDisclaimers instanceof Array 
        ? this.paymentMethodDisclaimers 
        : [this.paymentMethodDisclaimers];

      const disclaimer = disclaimers.find(dsc => parseInt(dsc.paymentTypeId) === pmtTypeId);
      this.set('paymentDisclaimerText', !!disclaimer ? disclaimer.text : '');
    }

    newSelectedCard.paymentTypeId = foundCard.id;
    this.selectedCard = newSelectedCard;
    if (this._shouldSelectedCardPushIntoPaymentList()) {
      this.push('paymentList', this.selectedCard);
    }

    this.viewSelectedPaymentMethod();
  }

  /**
   * Helper function to determine if we should push selectedCard into paymentList
   * @returns {boolean}
   */
  _shouldSelectedCardPushIntoPaymentList () {
    const paymentType = this.paymentTypesAllowed.find(t => t.id === this.selectedCard.paymentTypeId) || {};
    return !(paymentType.formName === "emptyPayment" || // Payment methods that use empty form should not be included in saved payments list
      this._isGetPaymentInList() ||
      this._isPutPaymentInList() ||
      this.paymentList.find(item => item.paymentTypeId === this.selectedCard.paymentTypeId));
  }
  /**
   * Check if the ADR payment is in the payment list
   */
  _isGetPaymentInList() {
    return this.selectedCard.CardNumber.includes('*') &&
      !!this.paymentList.find(item => item.expMonth === this.selectedCard.ExpirationMonth &&
        item.expYear === this.selectedCard.ExpirationYear.substring(2) &&
        item.paymentName === this.selectedCard.Name &&
        item.paymentNumber.substring(item.paymentNumber.length - 4) === this.selectedCard.CardNumber.substring(this.selectedCard.CardNumber.length - 4) &&
        item.paymentPostalCode === this.selectedCard.PostalCode &&
        (this.selectedCard.Type && item.paymentTypeDescription
          ? item.paymentTypeDescription.toLowerCase().includes(this.selectedCard.Type.toLowerCase())
          : false));
  }

  /**
   * Check if the ADR payment from put request is in the payment list
   */
  _isPutPaymentInList () {
    return !this.selectedCard.CardNumber.includes('*') &&
    !!this.paymentList.find(item => item.ccToken === this.selectedCard.CardNumber);
  }

  /**
   * Check if a card is a credit card
   * @param {Object} card
   */
  _isCreditCard (card) {
    return card.type === 'CCARD' || (this.creditCards && this.creditCards.some(obj => obj.id === card.paymentTypeId));
  }

  /**
   * Adds and assigns values to properties of the card object: expiration and lastFourDigits.
   * For selected cards only. Non-selected cards are handled separately, since the objects
   * returned by the ADR and save payment endpoints have differently named properties.
   */
  _selectedCardAdditionalData (card) {
    card.expiration = card.ExpirationMonth + '/' + card.ExpirationYear.slice(-2);
    card.lastFourDigits = card.CardNumber.slice(-4);
  }

  _selectedBankAdditionalData(card, payments) {
    const selectedBank = JSON.parse(JSON.stringify(payments[0].Bank));
    card.lastFourDigits = selectedBank.IBAN.slice(-4)
  }

  /**
   * Shows the default or selected payment method, and hides everything else.
   */
  viewSelectedPaymentMethod (e) {
    if (e && e.target.tagName === 'NS-BUTTON') {
      this._clearToastMessages();
    }
    this.currentView = 'selectedPayment';
  }

  /**
   * Shows the list of all credit cards, and hides everything else.
   */
  _viewAllPaymentMethods (e) {
    if (e && e.target.tagName === 'NS-BUTTON') {
      this._clearToastMessages();
    }
    this.editBillingErrorMessage = null;
    this.currentView = 'allPayments';
    this.selectedPaymentClassIndex = [0];
    this.top.scrollIntoView();
  }

  /**
   * Triggered when the user selects a payment from the list of available payments.
   * @param {*} event
   */
  _onSelectPayment(event) {
    const selectedPayment = event.model.item;
    if (selectedPayment.adrOnly) {
      this._updateADROnlyPayment(selectedPayment.orderPmtObject, selectedPayment);
    } else {
      this._applyPayment(selectedPayment, false);
    }
  }

  /**
   * Applies the given payment to the ADR and signals the ns-adp-wrapper to commit the change.
   * @param {PaymentDto} payment
   * @param {boolean} isNewPayment
   */
  _applyPayment(payment, isNewPayment = false) {
    const paymentSection = [];

    // Add entry to request removal of previous payment method from the ADR
    if (this.billingDataObject.payment.length > 0) {
      paymentSection.push({
        id: this.billingDataObject.payment[0].id,
        action: 'DELETE'
      });
    }

    // Add entry to request adding the new payment method to the ADR
    const paymentClass = this.paymentTypesAllowed.find(obj => obj.id === payment.paymentTypeId).paymentClass;
    if (!paymentClass) {
      // Payment type is unknown; abort
      return;
    }
    const pmntEntry = {
      type: paymentClass,
      action: 'ADD',
      amount: 0,
      Custom: []
    };

    // Apply data specific to the payment type
    switch (paymentClass) {
      case 'CCARD':
        pmntEntry.CreditCard = this.generateCreditCardBody(payment);
        break;
      case 'COMPANYCREDIT':
        pmntEntry.Custom.push({ Key: 'PAYMENT_TYPE', Value: payment.paymentTypeId.toString() });
        break;
      case 'DIRECTDEBIT':
        pmntEntry.Bank = {
          AccountHolderName: payment.paymentName,
          // The tokenized payment number is preferred when available, as the payment number is likely masked if it is present
          IBAN: payment.ccToken || payment.paymentNumber,
          BankCountry: payment.countryCode
        };
        if (isNewPayment) {
          pmntEntry.Custom.push({
            Key: 'FLAG_ELECTRONIC',
            Value: this.sepaMandateType === "electronic" ? "X" : "",
            Type: 'SEPA'
          });
        }
    }
    paymentSection.push(pmntEntry);

    this.billingDataObject.body = paymentSection;
    this.set('billingDataObject.updateType', 'Update');
    this._getSavedPayments();
  }

  /**
   * Performs an update of the ADR record for payments that only exist on the ADR record.
   * @param {OrderPayment} orderPayment Order Payment record of ADR-only payment
   * @param {PaymentDto} paymentDTO Data from payment update form
   */
  _updateADROnlyPayment(orderPayment, paymentDTO) {
    // Create a deep copy of the original payment record to mutate
    /** @type {OrderPayment} */
    const updatedOrderPayment = JSON.parse(JSON.stringify(orderPayment));

    switch (orderPayment.type) {
      case 'CCARD':
        updatedOrderPayment.CreditCard = Object.assign(
          {},
          this.generateCreditCardBody(paymentDTO),
          {
            CardNumber: orderPayment.CreditCard.CardNumber,
            EncryptedCardNumber: orderPayment.CreditCard.EncryptedCardNumber
          });
        break;
      case 'DIRECTDEBIT':
        updatedOrderPayment.Bank = Object.assign(orderPayment.Bank, {
          AccountHolderName: paymentDTO.paymentName
        });
        break;
    }

    updatedOrderPayment.action = "UPDATE";

    this.billingDataObject.body = [updatedOrderPayment];
    this.set('billingDataObject.updateType', 'Update');
  }

  /**
   * Generates a credit card entry for the ADR Update request from a Payment DTO
   * @param {PaymentDto} newCard
   */
  generateCreditCardBody (newCard) {
    let typeId = newCard.paymentTypeId >= 0 ? newCard.paymentTypeId : this.selectedPaymentType.id;
    return {
      CardNumber: newCard.ccToken,
      ExpirationMonth: newCard.expMonth,
      ExpirationYear: newCard.expYear ? '20' + newCard.expYear : "",
      Name: newCard.paymentName,
      PostalCode: newCard.paymentPostalCode,
      Type: this.paymentTypesAllowed.find(obj => obj.id === typeId).ofsId
    };
  }

  /**
   * on-click method for the 'Add' button at the bottom of the credit card list. Displays the form
   * for adding a new payment method, and hides everything else.
   */
  _showAddForm () {
    this._clearErrorMessages();
    this._clearToastMessages();
    this.currentView = 'paymentForm';
    this.paymentTypeId = this.selectedPaymentType.id;
    this.paymentType = this.selectedPaymentType.ofsId;

    // Show form based on payment type
    switch (this.selectedPaymentType.paymentClass) {
      case 'CCARD':
      case 'DIRECTDEBIT':
        this.cardNumberDisabled = false;
        this.formType = 'CCARD';
        break;
      case 'WIRE':
      case 'COMPANYCREDIT':
        this.formType = 'EMPTY';
        const otherMethods = this.otherPaymentMethods instanceof Array
          ? this.otherPaymentMethods
          : [this.otherPaymentMethods];
        const formData = otherMethods.find(m => parseInt(m.paymentTypeID) === this.paymentTypeId);
        if (formData) this.formHTML = formData.paymentMethodText;
        break;
    }

    this._resetForm();
    this.top.scrollIntoView();
  }

  /**
   * on-submit method for the add/edit payment method form. It knows whether it is a POST or PUT by
   * whether or not this.currentlyEditing is "truthy".
   * @param {Object} e the submit event
   */
  _addOrEditSaveChanges (e) {
    if (this.isDemo) {
      return;
    }
    e.preventDefault();

    // Payments that only exist on the ADR record require special handling for updates
    if (this.paymentFormData.adrOnly) {
      this._updateADROnlyPayment(this.paymentFormData.orderPmtObject, new PaymentDto(this._generateCard()));
      return;
    }

    switch (this.selectedPaymentType.paymentClass) {
      case 'CCARD':
      case 'DIRECTDEBIT':
        const generatedCardDto = new PaymentDto(this._generateCard());
        const nsPaymentForm = this.shadowRoot.querySelector('ns-payment');
        if (nsPaymentForm && nsPaymentForm.validate()) {
          this.currentlyEditing
            ? this._editCard(generatedCardDto)
            : this._addCard(generatedCardDto);
        }
        break;
      case 'WIRE':
      case 'COMPANYCREDIT':
        this._applyPayment(new PaymentDto({
          paymentTypeId: this.paymentTypeId
        }));
    }
  }

  /***
   * Calls the PaymentService, which makes a POST request.
   */
  _addCard (card) {
    if (this.isDemo) {
      return;
    }
    PaymentService.addPayment(card)
      .then(response => {
        this.postResponse = response;
        card["ccToken"] = response.ccToken;
        this._applyPayment(this.postResponse, true);
      })
      .catch(err => { this._handleError(err); });
  }

  /**
   * Calls the PaymentService, which makes a PUT request.
   */
  _editCard (card) {
    PaymentService.editSavedPayment(card)
      .then(response => {
        this.putResponse = response;
        card["ccToken"] = response.ccToken;
        this._applyPayment(this.putResponse, false);
      })
      .catch(err => { this._handleError(err); });
  }

  /**
   * on-click method for cancel button on add/edit payment form. Returns the view to the list of payment
   * methods.
   */
  _cancelAddOrEdit () {
    this._clearToastMessages();
    this._viewAllPaymentMethods();
    this.currentlyEditing = null;
    this._resetForm();
  }

  /**
   * on-click method for the delete button on each credit card. Displays a modal with an "are you
   * sure..." message, but does not change the payment list.
   * @param {Object} e the click event
   */
  _openDeleteModal (e) {
    this.currentlyDeleting = e.model.item.bePaymentTypeId;
    this.shadowRoot.querySelector('#deletePaymentModal').open();
  }

  /**
   * on-click method for the cancel button on the delete modal. Returns to the all payments view.
   */
  _closeDeleteModal () {
    this.currentlyDeleting = null;
    this.shadowRoot.querySelector('#deletePaymentModal').close();
  }

  /**
   * on-click method for the delete button on the delete modal. Whereas the credit card's delete button
   * only opens the modal, this button makes a service call to delete the credit card. It knows which
   * credit card to delete because the openDeleteModal method has previously set this.currentlyDeleting
   * to the ID of the card whose delete button was clicked to activate the modal.
   */
  _deleteSaveChanges () {
    PaymentService.deleteSavedPayment(this.currentlyDeleting)
      .then(response => {
        this.deleteResponse = response;
      })
      .catch(err => { this._handleError(err); });
  }

  /**
   * on-click method for the 'Edit' button on each credit card. Displays the form for editing an
   * existing payment method, and hides everything else.
   * @param {Object} e the click event
   */
  _showEditForm (e) {
    const item = e.model ? e.model.item : e;
    this._clearErrorMessages();
    this._clearToastMessages();
    this.paymentTypeId = item.paymentTypeId;
    this.paymentType = this.paymentTypesAllowed.find(obj => obj.id === item.paymentTypeId).ofsId;
    this.cardNumberDisabled = true;
    this.currentlyEditing = item.bePaymentTypeId;
    this._populateEditForm(item);
    this.currentView = 'paymentForm';
    this.formType = 'CCARD';
    this.formName = item.formName;
    this.top.scrollIntoView();
  }

  /**
   * Observer for this.getResponse. Extracts the payment list from the response and assigns it to this.paymentList.
   * @param {Object} response the HTTP response
   */
  _handleGetResponse (response) {
    this.savedPayments = response;
    if (!this.savedPayments || !this.savedPayments.length) {
      this.savedPayments = [];
    } else {
      this._generateAdditionalCardData();
    }
  }

  /**
   * observer method for this.postResponse. Adds the new credit card to the local payment list,
   * and makes it the selected payment method. Does not fetch a fresh copy of the list.
   * @param {Object} response the HTTP response
   */
  _handlePostResponse (response) {
    this._singleCardAdditionalData(response);
    response.isCreditCard = this._isCreditCard(response);
    this._singleCardExpiredData(response);
    this._viewAllPaymentMethods();
    this.push('paymentList', response);
    this.dispatchEvent(new CustomEvent('paymentChanged'));
  }

  /**
   * observer method for this.putResponse. Replaces the old card object in the local payment list
   * with the updated version, and makes it the selected payment method. Does not fetch a fresh copy of the list.
   * @param {Object} response the HTTP response
   */
  _handlePutResponse (response) {
    const editedPayment = response;
    this._singleCardAdditionalData(editedPayment);
    this._singleCardExpiredData(editedPayment);
    editedPayment.isCreditCard = this._isCreditCard(editedPayment);
    for (let card in this.paymentList) {
      if (this.paymentList[card].bePaymentTypeId === this.currentlyEditing) {
        this._viewAllPaymentMethods();
        this.set(['paymentList', card], editedPayment);
        break;
      }
    }
    this.currentlyEditing = null;
    this.dispatchEvent(new CustomEvent('paymentChanged'));
  }

  /**
   * observer method for this.deleteResponse. Deletes the credit card from the local payment list, and
   * does not fetch a fresh copy of the list.
   * @param {boolean} isOK - whether the response is successful or not
   */
  _handleDeleteResponse (isOK) {
    if (isOK) {
      for (let card in this.paymentList) {
        if (this.paymentList[card].bePaymentTypeId === this.currentlyDeleting) {
          this.splice('paymentList', card, 1);
          break;
        }
      }
      this.dispatchEvent(new CustomEvent('paymentChanged'));
      this._closeDeleteModal();
    }
  }

  /**
   * Computes and assigns the appropriate values to the lastFourDigits and expiration fields of
   * every card in the payment list.
   */
  _generateAdditionalCardData () {
    for (let card in this.savedPayments) {
      this._singleCardAdditionalData(this.savedPayments[card]);
    }
  }

  /**
   * Compares the payments on the ADR record against the list of user's saved payments,
   * adding payments from ADR record to list of available payments if not already present.
   * @param {BillingDataObject} billingDataObject Object containing
   * @param {PaymentDto[]} savedPayments
   * @param {PaymentTypeContext[]} paymentTypesAllowed
   */
  _compareADRPaymentsToList(billingDataObject, savedPayments, paymentTypesAllowed) {
    //return if any of the watched objects aren't set yet
    if (!billingDataObject || !billingDataObject.payment || !savedPayments || !paymentTypesAllowed) return;

    const payments = savedPayments.filter(p => !p.adrOnly);

    /**
     * @type {PaymentDto[]}
     */
    const adrPaymentDTOs = billingDataObject.payment
      .filter(p => !p.action) // Do not add payments that are part of outgoing requests
      .map(PaymentAdapter.mapSAPPaymentToPayment)
      .map(p => new PaymentDto(p))
      .map((dto, idx) => {
        // Mask payment number for display to user
        if (dto.paymentNumber) {
          dto.paymentNumber = "X".repeat(12) + dto.paymentNumber.slice(-4);
        }

        // Retrieve order payment object from which DTO was derived
        const adrOrderPmtObj = billingDataObject.payment[idx];

        // Apply app-specific additional attributes
        this._singleCardAdditionalData(dto);

        // Determine payment type ID from type information on order payment object
        const type = adrOrderPmtObj.type === "CCARD"
          ? paymentTypesAllowed.find(t => t.ofsId === adrOrderPmtObj.CreditCard.Type)
          : paymentTypesAllowed.find(t => t.ofsId === adrOrderPmtObj.type);

        if (type && !Number.isInteger(dto.paymentTypeId)) {
          dto.paymentTypeId = type.id;
        }

        dto.isCreditCard = this._isCreditCard(dto)
        this._singleCardExpiredData(dto, billingDataObject.nextDeliveryDate);
        return dto;
      });

    /*
    For each payment in ADR, attempt to find match in user's saved payments.
    If no match found, add payment to list and mark as ADR-only payment.
    */
    adrPaymentDTOs.forEach((adrPaymentDTO, idx) => {
      const matchingPayment = payments.find(dto => paymentsMatch(dto, adrPaymentDTO));
      if (!matchingPayment) {
        // Flag payment as ADR-only, and include reference to original order payment object
        adrPaymentDTO.adrOnly = true;
        adrPaymentDTO.orderPmtObject = billingDataObject.payment[idx];
        payments.unshift(adrPaymentDTO);
      }
    });

    this.set("paymentList", payments);
    this._mapPaymentTypes();
  }

  /**
   * Determines if a credit card's expiration date has passed.
   * @param {Object} card the credit card to be checked
   * @param {number} expTimestamp a timestamp that will compared to the card's expiration date.
   * This parameter is optional - if not specified, it defaults to the current date/time.
   * @return true if the card expires before the specified date, false if not
   */
  _checkExpired (card, expTimestamp) {
    /**
     * Date.getMonth has January as month 0, February as month 1, ...
     */
    const date = expTimestamp ? new Date(expTimestamp) : new Date();
    if (card.ExpirationMonth && card.ExpirationYear) {
      // It's a selected card from the ADR service.
      if (parseInt(card.ExpirationYear) === date.getFullYear()) {
        return parseInt(card.ExpirationMonth) < date.getMonth() + 1;
      }
      return parseInt(card.expYear) < date.getFullYear();
    } else {
      // It's a card object from the payment service.
      if (parseInt(card.expYear) === date.getFullYear() - 2000) {
        return parseInt(card.expMonth) < date.getMonth() + 1;
      }
      return parseInt(card.expYear) < date.getFullYear() - 2000;
    }
  }

  /**
   * Computes the properties isExpired, isExpiringSoon, and expirationDateIsOK for all cards in the payment list.
   * It uses the expiration month and year on each card in the payment list. isExpiringSoon is computed based on
   * the nextDeliveryDate in the billing data object. This method observers changes to both parameters.
   * billingDataObject is optional - if not passed in it will use the current expiration date instead of next delivery
   * date, and isExpiringSoon will always be false.
   * @param {Array} paymentList this.paymentList, the array of credit cards
   * @param {Object} this.billingDataObject
   */
  _computeExpiredData (paymentList, billingDataObject) {
    if (this.pageName === 'Adp Manage') {
      if (!(paymentList && billingDataObject)) {
        return;
      }
      for (let card in paymentList) {
        this._singleCardExpiredData(paymentList[card], billingDataObject.nextDeliveryDate);
      }
    } else {
      if (!paymentList) {
        return;
      }
      for (let card in paymentList) {
        this._singleCardExpiredData(paymentList[card]);
      }
    }
  }

  /**
   * Computes and assigns the properties isExpired, isExpiringSoon, and expirationDateIsOK for the
   * given card object, based on the card's expiration month and year. isExpiringSoon is computed based on
   * the specified nextDeliveryDate.
   * @param {Object} card
   * @param {number} nextDeliveryDate a timestamp of the delivery date we want to compare to the expiration date. This
   * parameter is optional - if not passed to checkExpired, it defaults to the current date.
   */
  _singleCardExpiredData (card, nextDeliveryDate) {
    card.isExpired = this._checkExpired(card);
    card.isExpiringSoon = !card.isExpired && this._checkExpired(card, nextDeliveryDate);
    card.expirationDateIsOK = !card.isExpired && !card.isExpiringSoon;
  }

  /**
   * Computes and assigns the appropriate values to the lastFourDigits and expiration fields of
   * the card object. Used for cards in the payment list, not the selected card (the properties have different
   * names).
   * @param {PaymentDto} card the card to be modified
   */
  _singleCardAdditionalData (card) {
    if (card.paymentNumber) card.lastFourDigits = card.paymentNumber.slice(-4);
    if (card.expMonth && card.expYear) card.expiration = card.expMonth + '/' + card.expYear;

  }

  /**
   * Reads information from an ns-payment component (i.e., the add/edit payment forms) and constructs a new
   * credit card object with that information
   * @return the new credit card object
   */
  _generateCard () {
    /**
     * @type {PaymentDto}
     */
    const card = {
      paymentName: this.paymentFormData.paymentName,
      paymentNumber: this.paymentFormData.paymentNumber,
      expMonth: this.paymentFormData.expMonth.toString(),
      expYear: this.paymentFormData.expYear.toString().slice(-2),
      address1: this.paymentFormData.address1,
      address2: this.paymentFormData.address2,
      city: this.paymentFormData.city,
      region: this.paymentFormData.region,
      paymentPostalCode: this.paymentFormData.paymentPostalCode,
      defaultPayment: this.paymentFormData.defaultPayment,
      bePaymentTypeId: this.currentlyEditing,
      paymentTypeId: this.paymentTypeId,
      paymentTypeDescription: this.paymentFormData.paymentTypeDescription,
      countryCode: this.paymentFormData.countryCode || this.country
    };
    if (this.currentlyEditing) {
      card.bePaymentTypeId = this.currentlyEditing;
    }
    return card;
  }

  /**
   * Fills the edit payment form with information from the selected credit card. The user can edit the information, but
   * the existing information will be used as a default.
   * @param {PaymentDTO} card the credit card whose data is to be used.
   */
  _populateEditForm (card) {
    this.paymentFormData = {
      paymentName: card.paymentName,
      paymentNumber: card.paymentNumber,
      paymentTypeDescription: card.paymentTypeDescription,
      expMonth:card.expMonth,
      expYear: card.expYear ? '20' + card.expYear : "",
      address1: card.address1,
      address2: card.address2,
      city: card.city,
      region: card.region,
      district: card.district,
      paymentPostalCode: card.paymentPostalCode,
      defaultPayment: card.defaultPayment,
      countryCode: card.countryCode,
      ccToken: card.ccToken,

      // Track additional properties applicable only to ADR-only payment records
      /** @type {boolean} */
      adrOnly: card.adrOnly,
      /** @type {OrderPayment} */
      orderPmtObject: card.orderPmtObject
    };
  }

  /**
   * Gets the form ready for adding a new card. Clears all data from the form so that the form will be empty.
   */
  _resetForm () {
    // set to empty strings instead of null: db complains about null values being set
    // more specifically for expMonth and expYear.
    this.paymentFormData = {
      paymentName: '',
      paymentNumber: '',
      expMonth: '',
      expYear: '',
      address1: '',
      address2: '',
      city: '',
      region: '',
      district: '',
      paymentPostalCode: '',
      defaultPayment: false,
      countryCode: this.country
    };
  }

  /**
   * Clears all error messages from the form so that it won't have any errors when the form is cancelled
   * and later reactivated.
   */
  _clearErrorMessages () {
    // NOTE: Using this instead of ns-form reset for now to handle hiding of error messages.
    // Replace with ns-form functionality once ns-form for polymer 3 is converted correctly
    /*******************************************************************************************/

    // Load the initial values.
    const nodes = this.shadowRoot.querySelector('ns-form-temp') ? this.shadowRoot.querySelector('ns-form-temp')._getSubmittableElements() : [];
    for (let node in nodes) {
      nodes[node].invalid = false;
    }
    this.paymentFormErrorMessage = null;
    /********************************************************************************************/
    // this.shadowRoot.querySelector('ns-form').reset(); // NOTE: Use this when ns-form reset polymer 3 conversion is fixed.
  }

  /**
   * Checks if the parameters prop and view are equal. This is used for hiding and showing dom-ifs.
   * For example, _isCurrentView(currentView, 'allPayments') checks whether or not the all payments view
   * should be displayed.
   * @param {String} prop
   * @param {String} view
   * @return true if prop and view match, false if not.
   */
  _isCurrentView (prop, view) {
    return prop === view;
  }

  /**
  * Used to close the expirationDialog
  */
  _closeCardExpirationDialog () {
    this.dispatchEvent(new CustomEvent('closeAdpWrapperOverlay', {detail: {closeOverlay: true}}));
    this.$.expirationDialog.close();
  }
  /**
  * Used to open billing to correct payment edit form and close expirationDialog after
  */
  _expirationDialogChangeView () {
    this._showEditForm(this.paymentList[0]);
    this._closeCardExpirationDialog();
  }

  /**
   * Observer for selectedCardTypeIndex. When that property changes, this method sets this.currentCardType
   * to the card type string corresponding to that index.
   */
  _dropdownSelectionChanged (e) {
    if (e) {
      this.selectedPaymentType = e;
    }
  }

  /**
   * Compares the specified string with the value of this.pageName. This is used in the billing.html file.
   * @param {String} pageName the page name to check against
   * @return true if it is a match, false if not
   */
  _isPageName (pageName) {
    return this.pageName === pageName;
  }

  /**
   * Map payment types
   */
  _mapPaymentTypes () {
    if (this.paymentTypesAllowed && this.billingDataObject && this.billingDataObject.payment[0]) {
      const adrPmnt = this.billingDataObject.payment[0];
      const isUsingWIRE = adrPmnt.CreditCard && adrPmnt.CreditCard.Type === 'WIRE';
      this.creditCards = this.paymentTypesAllowed.filter(obj => obj.paymentClass === 'CCARD');
      this.paymentTypes = this.paymentTypesAllowed.filter(obj => {
        let cedynaCheck = obj.id === 98 && this.paymentList && this.paymentList.find(obj => obj.paymentTypeId === 98)
        if (cedynaCheck) return obj.ofsId !== 'CEDN';
        else return obj.ofsId;
      }).filter(type => {
        // This filter regards WIRE payments only; allow all other types through
        if (type.paymentClass !== 'WIRE') return true;

        // Remove WIRE as option for new payments if it's already in use on the ADR
        if (isUsingWIRE) return false;

        // Remove WIRE as option for new payments if user already has a WIRE payment in their saved payments list
        return !this.paymentList || this.paymentList.every(pmt => pmt.paymentTypeId !== type.id);
      });
      this.selectedPaymentType = this.paymentTypes[0];
    }
  }

  /**
   * Returns the OFS ID of the given payment type.
   * Returns an empty string if the payment type cannot be found.
   * @param {number} typeId Payment type ID
   * @returns {string} Payment type OFS ID
   */
  _paymentTypeToOfsId(typeId) {
    return (this.paymentTypesAllowed.find(t => t.id === typeId) || {ofsId: ''}).ofsId
  }

  /**
   * Returns the localized name of the given payment type.
   * Returns an empty string if the payment type cannot be found.
   * @param {number} typeId - Payment type ID
   * @returns {string} Localized name of this payment type
   */
  _paymentTypeToLocalizedName (typeId) {
    return (this.paymentTypesAllowed.find(t => t.id === typeId) || { localizedName: '' }).localizedName;
  }

  _formButtonsClass(formType) {
    return this._isCurrentView(formType, 'EMPTY') ? 'empty-form-buttons' : '';
  }

  /**
   * Determines the correct button text for the use this payment button, based on whether the card is
   * expired, will expire before the next ship date, or neither.
   * @param {object} card
   * @return {string} The correct button text
   */
  _computeUseThisPaymentButtonText (card) {
    if (card.isExpired) {
      return this.expiredButtonText;
    }
    if (card.isExpiringSoon) {
      return this.expiringSoonButtonText;
    }
    return this.useThisPaymentButtonText;
  }

  /**
   * Adds a toast message for display to the user.
   * @param {string} type - message type: "success", "warning", "error", "information"
   * @param {Object} options - options:
   * @param {string} [options.header] - message header text
   * @param {string} [options.message] - message body text
   * @param {boolean} [options.scrollIntoView = false] - flag indicating whether to scroll to the toast message
   * @param {boolean} [options.showPrintOption = false] - flag indicating whether to show a print button
   */
  _addToastMessage(type, options = {}) {
    const DEFAULT_OBJECT = {
      type: 'information',
      header: '',
      scrollIntoView: false,
      showPrintOption: false
    };
    const messageObject = Object.assign({}, DEFAULT_OBJECT, options, {type});
    this.push('toastMessages', messageObject);
  }

  /**
   * Clears all toast messages from the page.
   */
  _clearToastMessages() {
    this.set('toastMessages', []);
  }

  _handleError (err) {
    if (err instanceof NsRestError) {
      err.messages.forEach(m => {
        console.error(m.technicalMessage, m);
        this._addToastMessage('error', {
          header: m.message,
          scrollIntoView: true
        });
      });
    } else {
      console.error(err);
      this._addToastMessage('error', {
        header: err.message,
        scrollIntoView: true
      });
    }
  }
}

customElements.define(NsBilling.is, NsBilling);

/**
 * Returns `true` if the two payments are equivalent, `false` otherwise.
 * @param {PaymentDTO} pmnt1
 * @param {PaymentDTO} pmnt2
 * @returns {boolean}
 */
function paymentsMatch(pmnt1, pmnt2) {
  const match = (a, b) => (!a && !b) || (a === b);
  const lastFour = a => a instanceof String ? a.substring(a.length - 4) : null;
  return match(
      lastFour(pmnt1.paymentNumber),
      lastFour(pmnt2.paymentNumber)
  ) && ([
      "paymentName",
      "paymentTypeId",
      "expMonth",
      "expYear"
  ]).every(prop => match(pmnt1[prop], pmnt2[prop]));
}

/**
 * @typedef BillingDataObject
 * @property {OrderPayment[]} payment List of order payments
 * @property {string} orderId ADR order ID
 * @property {string} timeStamp ADR order version timestamp
 * @property {number} nextDeliveryDate Next delivery date requested for this
 * ADR, as a UTC timestamp in milliseconds
 * @property {boolean} Editable Flag indicating whether the payment information
 * is editable
 */

/**
 * Object representing the payment applied to an order.
 * @typedef OrderPayment
 * @property {string} [id] Payment ID.
 * @property {string} type Payment type: "CASH", "CCARD", "COMPANYCREDIT",
 * "WIRE", "DIRECTDEBIT", "PAYD".
 * @property {number} amount Payment amount.
 * @property {string} action Action to take regarding this payment: "ADD",
 * "UPDATE", "DELETE". Applicable only to requests.
 * @property {string} Currency Currency code for the payment amount.
 * @property {string} AuthorizationStatus Payment authorization status.
 * @property {Messages} DeclinedMessage User message to show if this payment
 * is declined.
 * @property {CreditCard} CreditCard Credit card information associated with
 * this payment.
 * @property {Bank} Bank Bank information associated with this payment.
 * @property {Array<CustomField>} Custom Custom data fields applied to this
 * payment information.
 * @property {Array<Verification>} Verification
 * @property {BillingAddress} BillingAddress Billing address associated with
 * this payment.
 * @property {Array<ServerMessage>} Messages List of system messages.
 * @property {Authorization} AdditionalAuthorization
 */

/**
 *
 * @typedef ServerMessage
 * @property {string} Type
 * @property {string} Code
 * @property {string} Message
 * @property {string} BusinessData
 * @property {string} MsgParameter1
 * @property {string} MsgParameter2
 * @property {string} MsgParameter3
 * @property {string} MsgParameter4
 */

/**
 * Object representing a credit card.
 * @typedef CreditCard
 * @property {string} Name Name on this credit card.
 * @property {string} Type Credit card type. This field corresponds to
 * {@link PaymentTypeContext.paymentClass}.
 * @property {string} CardNumber Credit card number. Obfuscated if this is a
 * saved card.
 * @property {string} AuthorizationCode Authorization code for this credit
 * card.
 * @property {string} ExpirationMonth Two-digit expiration month. Example:
 * January is rendered as "01".
 * @property {string} ExpirationYear Four-digit expiration year. Example: 2023
 * is rendered as "2023".
 * @property {string} PostalCode Billing postal code for this credit card.
 * @property {string} VerificationCode Verification code for this credit card,
 * if applicable.
 * @property {string} [EncryptedCardNumber] Credit card number in encrypted
 * form. Applicable only to payments already saved in SAP.
 * @property {string} TrackOne
 * @property {string} Installments
 * @property {TransactionType} TransactionType
 */

/**
 *
 * @typedef TransactionType
 * @property {string} value
 * @property {number} PaymentPeriod
 */

/**
 * Object representing the bank associated with a payment.
 * @typedef Bank
 * @property {string} BankCountry Two-letter country code of bank country
 * @property {string} BankKey
 * @property {string} BankControlKey
 * @property {string} BankAccount
 * @property {string} BankName
 * @property {string} BankBranch
 * @property {string} AccountHolderName Name of bank account holder
 * @property {string} IBAN International Bank Account Number
 * @property {string} Type
 */

/**
 *
 * @typedef Verification
 * @property {string} Key
 * @property {string} Value
 * @property {string} Provider
 */

/**
 *
 * @typedef BillingAddress
 * @property {string} address Billing street address
 * @property {string} district Billing district/county
 * @property {string} city Billing city
 * @property {string} country Billing country (two-letter country code)
 * @property {string} state Billing state/region
 */

/**
 *
 * @typedef Authorization
 * @property {number} AuthTimeStamp
 * @property {string} AuthorizationReference
 * @property {string} TransactionReference
 * @property {string} MerchantID
 * @property {string} Result
 * @property {string} ResultText
 * @property {string} ResultCode
 * @property {string} ResultACI
 * @property {string} ResultCVV
 * @property {string} DelegoResponseCode
 * @property {string} DelegoReference
 */

/**
 * Object representing a payment saved to a user's profile.
 * @typedef PaymentDTO
 * @property {number} [bePaymentTypeId] ID of this payment. This value is
 * added by the server and should not be altered by the client.
 * @property {number} paymentTypeId ID of the payment type associated with
 * this payment.
 * @property {string} [paymentTypeDescription] Description of the payment type
 * associated with this payment.
 * @property {string} paymentName Payment name.
 * @property {string} paymentNumber String representing the payment number
 * (e.g. credit card number). Saved payments may have this number masked.
 * @property {string} [ccToken] Tokenized payment number. This value is added
 * by the server and should not be altered by the client.
 * @property {string} [expMonth] String representing the two-digit expiry
 * month.
 * @property {string} [expYear] String representing the two-digit expiry year.
 * @property {string} [address1] First line of billing address.
 * @property {string} [address2] Second line of billing address.
 * @property {string} [city] Billing city.
 * @property {string} [district] Billing district/county.
 * @property {string} [region] Billing region/state.
 * @property {string} [countryCode] Two-letter country code of billing country.
 * @property {string} [paymentPostalCode] Billing postal code.
 * @property {boolean} [defaultPayment] Flag indicating whether this is the
 * user's default payment.
 */

/**
 * @typedef PaymentTypeContext
 * @property {number} id
 * @property {string} ofsId
 * @property {string} paymentClass
 * @property {string} localizedName
 * @property {string} formName
 * @property {string} ccImageUrl
 * @property {boolean} blocked
 * @property {boolean} disabled
 * @property {string} processorCode
 * @property {string} merchantId
 * @property {boolean} installmentsAllowed
 * @property {number[]} installmentNumbers
 * @property {number} minimumInstallmentAmount
 * @property {number} simulateWhenNumberGT
 * @property {boolean} binListInstallmentCheck
 */
